import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';

import { useAttributeListAtom } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';

import { filterPredicateIsTruthy } from 'utility/Check/filterPredicateIsTruthy';
import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { Attribute } from 'proto/Accommodation/Attributes';

import styles from '../../Filter.module.scss';
import { attributeListMap } from '../../ScopedAttribute/__config/mapping';
import {
    MetaValue,
    useDisabledStatus,
} from '../../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../../Segment/FilterLabel';

const Attributes = (attributes: Attribute[], disableWhen = MetaValue.Never) => {
    const { t } = useTranslation();
    const [attributeList, setAttributeList] = useAttributeListAtom();
    const [accommodationMeta] = useAccommodationMeta();
    const { onToggle, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);

    const onSelect = useCallback(
        (selection: Attribute) => {
            onToggle(selection);
            setAttributeList((current) => toggleValueInSet(current, selection));
        },
        [setAttributeList, onToggle]
    );

    return attributes
        .filter(
            (attribute) =>
                !!accommodationMeta?.attributes?.[attribute]?.hasCount ||
                !!accommodationMeta?.attributes?.[attribute]?.isPotential
        )
        .map((attribute) => {
            const isChecked = attributeList.has(attribute);
            const meta = accommodationMeta?.attributes?.[attribute];
            const label = t(attributeListMap[attribute] ?? 'TRANS');
            const tooltip =
                attribute === Attribute.HandicappedAccessible
                    ? `<b>${label}</b>${t(
                          'TRANS-you-can-find-accessible-hotels'
                      )}`
                    : '';

            return {
                filterValue: label,
                content: (
                    <Checkbox
                        key={attribute}
                        input={{
                            id: `attribute_${attribute}`,
                            value: attribute,
                            name: `attribute_${attribute}`,
                        }}
                        isChecked={isChecked}
                        isDisabled={
                            !isChecked && meta && getDisabledStatus(meta)
                        }
                        isOnlyDeselectable={
                            isChecked && meta?.hasCount && meta?.count === 0
                        }
                        onChange={() => onSelect(attribute)}
                        withUXSelection
                        customClass={{
                            Wrapper: classNames({
                                [styles.Changed]: isFilterChanged(attribute),
                            }),
                        }}
                    >
                        <FilterLabel
                            labelText={label}
                            tooltip={tooltip}
                            {...meta}
                        />
                    </Checkbox>
                ),
                isSelected: isChecked,
            };
        })
        .filter(filterPredicateIsTruthy);
};

export default Attributes;
